<template>
  <div v-if="fetched" class="wrapper">
    <div v-if="!errored" class="container">
      <!-- バナー -->
      <div class="banner">
        <img :src="gacha.bannerUrl" />
        <div class="origin" v-if="gacha.original">
          <p v-if="gacha.original === 'F001'">
            {{ $t('common__label__avatar_original_f') }}
          </p>
          <p v-else-if="gacha.original === 'M001'">
            {{ $t('common__label__avatar_original_m') }}
          </p>
        </div>
      </div>
      <div class="cancellationCautionText">
        {{ $t('avatar_gacha_rates__caption__image_caution') }}
      </div>
      <div class="content">
        <!-- 提供割合タブ -->
        <div class="tabGroupContent" v-if="isRarityGuaranteedGacha">
          <div
            v-for="weightSetId in gachaGuaranteeWeightSetIds"
            :key="weightSetId"
            class="tabGroup"
          >
            <button
              class="tabWeight"
              v-bind:class="{
                tabSelectedWeight: weightSetId === selectedWeight,
              }"
              @click="tapGroupButton(weightSetId)"
            >
              <span>{{ $t(getTabNameKey(weightSetId)) }}</span>
            </button>
          </div>
        </div>
        <!-- テーブル -->
        <div v-if="!loading">
          <table class="gachaContentList">
            <tbody>
              <template v-for="(gachaItemViewRarity, i) in gachaItemsRarity">
                <!-- レア度ヘッダー -->
                <tr :key="i" class="rarityHeader">
                  <td colspan="3" class="rarityHeaderStar">
                    <img
                      v-for="star in gachaItemViewRarity.rarity"
                      :key="star"
                      src="/img/rarity-star.png"
                      width="16px"
                      height="16px"
                    />
                  </td>
                  <td class="rarityHeaderTotalEmissionRate">
                    {{
                      gachaRarityRateMap[gachaItemViewRarity.rarity].toFixed(2)
                    }}%
                  </td>
                </tr>
                <!-- アイテムテーブル -->
                <template
                  v-for="(
                    gachaItemTotalColor, j
                  ) in gachaItemViewRarity.gachaItemViewTotalColor"
                >
                  <tr
                    v-for="(gachaItem, k) in gachaItemTotalColor.gachaItemView"
                    :key="gachaItem.itemId"
                    :class="{
                      gachaContent:
                        j !=
                          gachaItemViewRarity.gachaItemViewTotalColor.length -
                            1 ||
                        k != gachaItemTotalColor.gachaItemView.length - 1,
                    }"
                  >
                    <!-- サムネ -->
                    <td class="thumbnail">
                      <img :src="gachaItem.suitablePartsSet.itemImageUrl" />
                    </td>
                    <!-- アイテム名 -->
                    <td class="name">
                      {{ gachaItem.name }}
                    </td>
                    <!-- 排出率 -->
                    <td class="emissionRate">
                      {{ gachaItem.formattedEmissionRate.toFixed(2) }}%
                    </td>
                    <!-- 合計排出率 -->
                    <td
                      :class="{
                        totalColorEmissionRate: true,
                        totalColorEmissionRateBorder:
                          j !=
                          gachaItemViewRarity.gachaItemViewTotalColor.length -
                            1,
                      }"
                      v-if="k == 0"
                      :rowspan="gachaItemTotalColor.gachaItemView.length"
                    >
                      <span class="totalRate">
                        {{ $t('gachaClosetRatesTotal') }}
                      </span>
                      {{ gachaItemTotalColor.totalEmissionRate.toFixed(2) }}%
                    </td>
                  </tr>
                </template>
              </template>
            </tbody>
          </table>
          <ul v-if="!this.loading" class="caution">
            <li>{{ $t('gachaDesc1') }}</li>
            <li>{{ $t('gachaDesc2') }}</li>
            <li>{{ $t('gachaDesc3') }}</li>
            <li>{{ $t('gachaDesc9') }}</li>
            <li>{{ $t('gachaDesc4') }}</li>
            <li>{{ $t('gachaDesc5') }}</li>
            <li>{{ $t('gachaDesc6') }}</li>
            <li>{{ $t('gachaDesc7') }}</li>
            <li>{{ $t('gachaDesc10') }}</li>
            <li>{{ $t('gachaDesc8') }}</li>
            <li>{{ $t('gachaDesc11') }}</li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import 'reflect-metadata'
import { Component, Mixins } from 'vue-property-decorator'
import {
  ApiGachaItemEmissionRate,
  ApiAvatarPartsSet,
  ApiGachaListReq,
  ApiGachaListCallFromOdds,
} from '../../api/schema/appGatewayServer'
import { Route } from 'vue-router'
import i18n from '@/i18n'
import { LanguageQueryHandler } from '@/mixins/LanguageQuery'
import { apiV2 } from '@/api/appGatewayServerV2Api'
import { Gacha } from '@/api/pb/Gacha/Common/Gacha'
class GachaNotFound extends Error {}

interface GachaItemView extends ApiGachaItemEmissionRate {
  suitablePartsSet: ApiAvatarPartsSet
  formattedEmissionRate: number
}

interface GachaItemViewTotalColor {
  gachaItemView: GachaItemView[]
  totalEmissionRate: number
}

interface GachaItemViewTotalRarity {
  gachaItemViewTotalColor: GachaItemViewTotalColor[]
  rarity: number
}

interface RarityGuaranteeView {
  type: RarityGuaranteeType
  weightSetId: number
}

const rarityGuaranteeTypes = {
  Default: 0, // 通常のレアリティ排出率
  Rarity3And4: 1, // レアリティ3以上確定
  Rarity3And4Up: 2, // レアリティ3以上確定星4確率アップ
  Rarity4: 3, // レアリティ4確定
  Rarity3Only: 4, // レアリティ3確定
} as const
type RarityGuaranteeType =
  (typeof rarityGuaranteeTypes)[keyof typeof rarityGuaranteeTypes]

class GachaItemUtil {
  static gachaItemToView(
    gacha: Gacha,
    gachaItem: ApiGachaItemEmissionRate
  ): GachaItemView {
    const suitablePartsSet = this.suitablePartsSet(gacha.original, gachaItem)
    const formattedEmissionRate = gachaItem.emissionRate
    return {
      ...gachaItem,
      suitablePartsSet,
      formattedEmissionRate,
    }
  }

  static suitablePartsSet(
    original: string,
    gachaItem: ApiGachaItemEmissionRate
  ) {
    for (const partsSet of gachaItem.partsSets) {
      if (partsSet.original === original) return partsSet
    }
    return gachaItem.partsSets[0]
  }
}

@Component({
  title: i18n.t('gachaOdds'),
} as any)
export default class GachaOdds extends Mixins(LanguageQueryHandler) {
  gachaId: number = 0
  isRarityGuaranteedGacha = false

  fetched = false
  errored = false
  gacha = {} as Gacha
  gachaItems: GachaItemView[] = []
  gachaItemsPartsSetIdMap: { [key: number]: GachaItemViewTotalColor } = {}
  gachaItemsRarityMap: { [key: number]: GachaItemViewTotalRarity } = {}
  gachaItemsRarity: GachaItemViewTotalRarity[] = []
  gachaRarityRateMap: { [key: number]: number } = {}
  gachaGuaranteeMap: { [key: number]: RarityGuaranteeView } = {}
  gachaGuaranteeWeightSetIds: number[] = []
  defaultWeightSetId: number = 0

  $route!: Route
  $t!: any

  selectedWeight: number = 0
  loading: boolean = true

  private getTabNameKey(tapWeight: number): string {
    const guaranteeType = this.gachaGuaranteeMap[tapWeight].type
    if (guaranteeType === rarityGuaranteeTypes.Default) {
      return 'gacha_closet__rates__category__generally'
    } else if (guaranteeType === rarityGuaranteeTypes.Rarity3And4) {
      return 'gacha_closet__rates__category__3+guaranteed'
    } else if (guaranteeType === rarityGuaranteeTypes.Rarity3And4Up) {
      return 'gacha_closet__rates__category__3+4rate_guaranteed'
    } else if (guaranteeType === rarityGuaranteeTypes.Rarity4) {
      return 'gacha_closet__rates__category__4guaranteed'
    } else if (guaranteeType === rarityGuaranteeTypes.Rarity3Only) {
      return 'gacha_closet__rates__category__3guaranteed'
    }
    return ''
  }

  private async mounted() {
    this.gachaId = Number(this.$route.params.gachaId)

    try {
      await this.fetchGachaList()
      await this.fetchGachaItems()
      this.loading = false
    } catch (error) {
      this.errored = true
    } finally {
      this.fetched = true
    }
  }

  // ウェイトタブ切り替え時に実行する
  private async tapGroupButton(tapWeight: number) {
    if (this.selectedWeight === tapWeight) return
    if (this.loading) return
    this.loading = true
    this.selectedWeight = tapWeight

    // 初期化
    this.gachaItems = []
    this.gachaItemsPartsSetIdMap = {}
    this.gachaItemsRarityMap = {}
    this.gachaItemsRarity = []
    this.gachaRarityRateMap = {}

    // weightに対応した排出率を取得する
    try {
      await this.fetchGachaItems()
    } catch (error) {
      this.errored = true
    } finally {
      this.loading = false
    }
  }

  private async fetchGachaList() {
    const req: ApiGachaListReq = {
      from: ApiGachaListCallFromOdds,
    }
    const gachaListResponse = await apiV2().gacha.list(req)
    const gacha = gachaListResponse.gachaList.find(
      (gacha) => gacha.id === this.gachaId
    )
    if (!gacha) throw new GachaNotFound()
    this.gacha = gacha

    // レア度以上確定ガチャでない場合は早期return
    if (gacha.rarityGuaranteeSetId <= 0) {
      return
    }

    // レア度以上確定ガチャの場合はRarityGuaranteeWeightSetIDを取り出す
    const guarantee = gachaListResponse.gachaRarityGuaranteeList.find(
      (guarantee) => guarantee.setId === gacha.rarityGuaranteeSetId
    )
    if (!guarantee) return

    this.isRarityGuaranteedGacha = true

    // レアリティレートを取り出して整形する
    const guaranteeList = guarantee.rarityGuarantee
    this.gachaGuaranteeMap[this.defaultWeightSetId] = {
      type: 0,
      weightSetId: 0,
    }
    guaranteeList.forEach((guarantee) => {
      this.gachaGuaranteeMap[guarantee.weightSetId] = {
        type: guarantee.type as RarityGuaranteeType,
        weightSetId: guarantee.weightSetId,
      }
    })
    for (const weightSetId in this.gachaGuaranteeMap) {
      this.gachaGuaranteeWeightSetIds.push(Number(weightSetId))
    }
  }

  private async fetchGachaItems() {
    const itemResponse = await apiV2().gacha.emissionItem.list({
      gachaId: this.gachaId,
      rarityGuaranteeWeightSetId: this.selectedWeight,
    })

    // レアリティレートを取り出す
    itemResponse.rarityRates.forEach((gachaRarityRate) => {
      this.gachaRarityRateMap[gachaRarityRate.rarity] =
        gachaRarityRate.emissionRate
    })

    // アイテム一覧を取り出す
    this.gachaItems = itemResponse.gachaItems.map((gachaItem) =>
      GachaItemUtil.gachaItemToView(this.gacha, gachaItem)
    )

    // 色ごとにまとめる
    this.gachaItems.forEach((gachaItem) => {
      if (
        this.gachaItemsPartsSetIdMap[gachaItem.suitablePartsSet.id] ===
        undefined
      ) {
        this.gachaItemsPartsSetIdMap[gachaItem.suitablePartsSet.id] = {
          gachaItemView: [],
          totalEmissionRate: 0,
        }
      }
      this.gachaItemsPartsSetIdMap[
        gachaItem.suitablePartsSet.id
      ].gachaItemView.push(gachaItem)
      this.gachaItemsPartsSetIdMap[
        gachaItem.suitablePartsSet.id
      ].totalEmissionRate += gachaItem.emissionRate
    })

    // レア度ごとにまとめる
    Object.keys(this.gachaItemsPartsSetIdMap).forEach((id) => {
      var rarity =
        this.gachaItemsPartsSetIdMap[parseInt(id)].gachaItemView[0].rarity
      if (this.gachaItemsRarityMap[rarity] === undefined) {
        this.gachaItemsRarityMap[rarity] = {
          gachaItemViewTotalColor: [],
          rarity: 0,
        }
      }
      this.gachaItemsRarityMap[rarity].gachaItemViewTotalColor.push(
        this.gachaItemsPartsSetIdMap[parseInt(id)]
      )
      this.gachaItemsRarityMap[rarity].rarity = rarity
    })

    // レア度でソート
    this.gachaItemsRarity = Object.keys(this.gachaItemsRarityMap)
      .map((rarity) => {
        var key = parseInt(rarity)
        return this.gachaItemsRarityMap[key]
      })
      .sort((a, b) => {
        return b.rarity - a.rarity
      })
  }
}
</script>

<style scoped>
.wrapper {
  background: #f8f8f8;
  min-height: 100%;
}

.container {
  max-width: 500px;
  margin: 0 auto;
  padding: 0 0 var(--safe-area-inset-bottom, 0px) 0;
  background: #fff;
}

.banner {
  width: 100%;
}

.banner p {
  position: absolute;
  font-size: 10px;
  font-weight: bold;
  text-align: center;
  border-radius: 0px 0px 4px 0px;
  margin: 0 0 0 0;
  padding: 2px 8px;
  background-color: #ff003d;
  color: #ffffff;
}

.banner img {
  width: 100%;
  text-align: center;
  vertical-align: bottom;
  background-color: #ff003d;
}

.origin {
  position: relative;
}

.origin p {
  position: absolute;
  right: 0;
  bottom: 0;
  margin-right: 8px;
  margin-bottom: 8px;
  padding: 2px 8px;
  border-radius: 4px;
  background: rgba(38, 38, 38, 0.6);
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.4px;
  text-align: center;
  color: #ffffff;
}

.cancellationCautionText {
  padding: 4px 16px 8px 16px;
  text-align: center;
  font-size: 10px;
  line-height: 16px;
  color: #bababa;
}

.gachaContentList {
  width: 100%;
  border-bottom: 1px solid #e8e8e8;
  border-collapse: collapse;
}

.gachaContentHeader {
  text-align: left;
  font-size: 0.9rem;
  line-height: 2.3rem;
}

.gachaContentHeaderColorTotalRarity {
  text-align: center;
  font-size: 0.9rem;
  line-height: 1rem;
}

.rarityHeader {
  width: 100%;
  height: 32px;
  text-align: center;
  background-color: #f6f6f6;
  vertical-align: middle;
}

.rarityHeaderStar {
  text-align: left;
  padding: 4px 0px 7px 16px;
}

.rarityHeaderStar img {
  padding-right: 4px;
}

.rarityHeaderTotalEmissionRate {
  text-align: right;
  vertical-align: middle;
  line-height: 16px;
  font-size: 12px;
  display: inline-block;
  padding: 10px 16px 8px 8px;
  width: 100%;
}

.gachaContent {
  margin: 0px 0px 0px 0px;
}

.gachaContent td:not(.totalColorEmissionRate) {
  border-bottom: 1px solid #e8e8e8;
}

.thumbnail {
  width: 44px;
  height: 72px;
  padding-left: 16px;
}

.thumbnail img {
  width: 44px;
  height: 44px;
}

.name {
  width: 164px;
  font-size: 12px;
  overflow-wrap: break-word;
  padding-left: 12px;
}

.emissionRate {
  width: 68px;
  line-height: 16px;
  font-size: 12px;
  text-align: center;
  padding-left: 12px;
}

.totalColorEmissionRate {
  width: 55px;
  line-height: 18px;
  font-size: 12px;
  text-align: center;
  padding-right: 16px;
}

.totalColorEmissionRateBorder {
  border-bottom: 1px solid #e8e8e8;
}

.totalRate {
  font-size: 10px;
  color: #bababa;
  line-height: 15px;
  text-align: center;
  display: block;
}

.color {
  text-align: center;
}

.caution {
  margin: 1rem auto;
  font-size: 12px;
  list-style: none;
  padding: 0 16px 0 24px;
}

.caution li:before {
  content: '＊';
  margin-left: -1em;
  color: #808080;
}

.tabGroupContent {
  height: 40px;
  max-width: 500px;
  margin: 0 auto;
  overflow: scroll;
  white-space: nowrap;
}

.tabGroupContent::-webkit-scrollbar {
  display: none;
}

.tabGroup {
  line-height: 40px;
  display: inline-block;
  height: 40px;
  font-size: 14px;
  color: #000;
  text-align: center;
}

.tabWeight {
  width: 100%;
  height: 40px;
  padding: 0px 8px 0px 8px;
  font-style: normal;
  font-weight: 600;
  font-size: 12px;
  line-height: 140%;
  display: flex;
  align-items: center;
  text-align: center;
  color: #bababa;
  flex: none;
  order: 0;
  flex-grow: 0;
  background-color: Transparent;
  border: none;
  border-bottom: 3px solid transparent;
}

.tabSelectedWeight {
  width: 100%;
  height: 40px;
  padding: 0px 8px 0px 8px;
  font-style: normal;
  font-weight: 600;
  font-size: 12px;
  line-height: 140%;
  display: flex;
  align-items: center;
  text-align: center;
  color: #188dff;
  flex: none;
  order: 0;
  flex-grow: 0;
  background-color: Transparent;
  border: none;
  border-bottom: 3px solid;
}
</style>
